import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/shared/ui/PageHeader";
import { CampoCerca } from "@/shared/ui/ChipFiltro";
import {
  Foglio,
  GiornataAutomatismi,
  Indice,
  Pillola,
  Sezione,
  TASTO,
  type Tono,
  useSezioneVisibile,
} from "@/features/impostazioni/parti";
import {
  type Chi,
  GIRO_ORDINE,
  GLOSSARIO,
  normalizza,
  PAGINE,
  type Pagina,
  REGOLE_ORO,
  SE_NON_VA,
} from "./contenuti";

// Guida di OM (ridisegno 28/10/2026). Si legge dall'alto: prima il giro di
// un ordine e le regole che fanno danni se non le sai, poi una sezione per
// pagina, poi i guasti e le parole. La ricerca attraversa tutto.

const SEZIONI = [
  { id: "giro", nome: "Come gira un ordine" },
  { id: "giornata", nome: "La giornata" },
  { id: "regole", nome: "Regole d'oro" },
  ...PAGINE.map((p) => ({ id: p.id, nome: p.nome })),
  { id: "guasti", nome: "Se qualcosa non va" },
  { id: "parole", nome: "Le parole di OM" },
];

const CHI: Record<Chi, { testo: string; tono: Tono }> = {
  solo: { testo: "Da solo", tono: "ok" },
  mano: { testo: "A mano", tono: "attenzione" },
  misto: { testo: "Da solo o a mano", tono: "info" },
};

export function IstruzioniPage() {
  const [cerca, setCerca] = useState("");
  const [attivo, setAttivo] = useSezioneVisibile(
    SEZIONI.map((s) => s.id),
    "giro",
  );
  const q = normalizza(cerca.trim());

  return (
    <main className="flex-1 overflow-y-auto px-7 pb-24 pt-[22px] scroll-paper max-md:px-3 max-md:pb-10 max-md:pt-3">
      <PageHeader
        title="Istruzioni"
        subtitle="Come si usa OM, pagina per pagina. Cosa fa da solo e cosa fai tu."
      />

      <div className="mx-auto grid max-w-[1280px] grid-cols-[minmax(0,1fr)] gap-8 max-md:gap-4 md:[grid-template-columns:184px_minmax(0,1fr)]">
        <Indice
          voci={SEZIONI}
          attivo={attivo}
          onScegli={(id) => {
            setCerca("");
            setAttivo(id);
          }}
          etichetta="Sezioni della guida"
        />

        <div className="min-w-0 space-y-12 max-md:space-y-9">
          <CampoCerca
            value={cerca}
            onChange={setCerca}
            placeholder="Cerca nella guida: LDV, storno, ritiro…"
            ariaLabel="Cerca nella guida"
            className="w-full max-w-[520px]"
          />

          {q ? (
            <Risultati q={q} />
          ) : (
            <>
              <Sezione
                id="giro"
                titolo="Come gira un ordine"
                sottotitolo="Sei tappe, dall'ordine del cliente alla fattura. Accanto a ogni tappa: chi la fa."
              >
                <GiroOrdine />
              </Sezione>

              <Sezione
                id="giornata"
                titolo="La giornata"
                sottotitolo="Gli orari fissi di OM. Il resto gira ogni pochi minuti, tutto il giorno."
              >
                <Foglio>
                  <GiornataAutomatismi />
                </Foglio>
              </Sezione>

              <Sezione
                id="regole"
                titolo="Regole d'oro"
                sottotitolo="Se non le sai, fai danni veri: merce comprata due volte, fatture sbagliate, clienti avvisati per niente."
              >
                <RegoleOro />
              </Sezione>

              {PAGINE.map((p) => (
                <SezionePagina key={p.id} pagina={p} />
              ))}

              <Sezione
                id="guasti"
                titolo="Se qualcosa non va"
                sottotitolo="I problemi più comuni e cosa fare, in ordine."
              >
                <Guasti />
              </Sezione>

              <Sezione
                id="parole"
                titolo="Le parole di OM"
                sottotitolo="Le sigle che trovi in giro per le pagine."
              >
                <Glossario />
              </Sezione>
            </>
          )}
        </div>
      </div>
    </main>
  );
}

function GiroOrdine() {
  return (
    <ol className="grid grid-cols-6 gap-0 max-xl:grid-cols-3 max-xl:gap-y-6 max-md:grid-cols-1 max-md:gap-y-0">
      {GIRO_ORDINE.map((t, i) => (
        <li
          key={t.nome}
          className="relative flex flex-col pr-5 max-md:flex-row max-md:gap-4 max-md:pb-6 max-md:pr-0"
        >
          {/* La linea che unisce le tappe: orizzontale sul computer,
              verticale sul telefono. */}
          {i < GIRO_ORDINE.length - 1 && (
            <span
              aria-hidden="true"
              className="absolute left-9 right-0 top-[15px] h-px bg-line-2 max-xl:hidden max-md:block max-md:bottom-0 max-md:left-[15px] max-md:right-auto max-md:top-9 max-md:h-auto max-md:w-px"
            />
          )}
          <span className="relative z-[1] flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-ink font-display text-[14px] font-bold tabular-nums text-on-ink">
            {i + 1}
          </span>
          <div className="mt-3 min-w-0 max-md:mt-0.5">
            <h3 className="font-display text-[17px] font-bold leading-tight text-ink">{t.nome}</h3>
            <div className="mt-1.5">
              <Pillola tono={CHI[t.chi].tono}>{CHI[t.chi].testo}</Pillola>
            </div>
            <p className="mt-2 text-[13px] leading-relaxed text-ink max-md:text-[15px]">
              {t.testo}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function RegoleOro() {
  return (
    <Foglio>
      <ol className="grid grid-cols-2 max-lg:grid-cols-1">
        {REGOLE_ORO.map((r, i) => (
          <li
            key={r.titolo}
            className="flex gap-4 border-line px-5 py-4 max-md:px-4 lg:[&:nth-child(odd)]:border-r [&:nth-child(n+3)]:border-t max-lg:[&:nth-child(n+2)]:border-t"
          >
            <span className="w-6 shrink-0 font-display text-[20px] font-bold leading-none tabular-nums text-accent-2">
              {i + 1}
            </span>
            <div className="min-w-0">
              <p className="text-[14px] font-bold leading-snug text-ink max-md:text-[16px]">
                {r.titolo}
              </p>
              <p className="mt-1 text-[13px] leading-relaxed text-ink-2 max-md:text-[15px] max-md:text-ink">
                {r.perche}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </Foglio>
  );
}

function SezionePagina({ pagina: p }: { pagina: Pagina }) {
  return (
    <Sezione
      id={p.id}
      titolo={p.nome}
      sottotitolo={p.serve}
      azioni={p.apri.map((a) => (
        <Link key={a.href} to={a.href} className={`${TASTO} no-underline`}>
          Apri {a.nome}
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-3.5 w-3.5 fill-none stroke-current stroke-2 [stroke-linecap:round] [stroke-linejoin:round]"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </Link>
      ))}
    >
      <Foglio>
        {p.daSolo && (
          <div className="rounded-t-lg border-b border-line bg-green-soft/60 px-5 py-3 max-md:rounded-t-2xl max-md:px-4">
            <p className="text-[12px] font-bold uppercase tracking-[0.06em] text-ink">
              OM fa da solo
            </p>
            <ul className="mt-1.5 grid gap-x-6 gap-y-1 text-[13px] text-ink max-md:text-[15px] lg:grid-cols-2">
              {p.daSolo.map((d) => (
                <li key={d} className="flex gap-2">
                  <span
                    aria-hidden="true"
                    className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-green"
                  />
                  {d}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="grid grid-cols-2 max-lg:grid-cols-1">
          {p.voci.map((v, i) => (
            <div
              key={v.titolo}
              className={[
                "border-line px-5 py-4 max-md:px-4",
                i % 2 === 0 ? "lg:border-r" : "",
                i >= 2 ? "border-t" : i === 1 ? "max-lg:border-t" : "",
                // L'ultima voce dispari prende tutta la riga: niente buchi.
                i === p.voci.length - 1 && i % 2 === 0 ? "lg:col-span-2 lg:border-r-0" : "",
              ].join(" ")}
            >
              <h3 className="text-[14px] font-bold leading-snug text-ink max-md:text-[16px]">
                {v.titolo}
              </h3>
              <ol className="mt-2 space-y-1.5">
                {v.passi.map((passo, n) => (
                  <li
                    key={passo}
                    className="flex gap-2.5 text-[13px] leading-relaxed text-ink max-md:text-[15px]"
                  >
                    <span className="w-4 shrink-0 text-right font-bold tabular-nums text-ink-3 max-md:text-ink-2">
                      {n + 1}
                    </span>
                    <span className="min-w-0">
                      <Virgolette testo={passo} />
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      </Foglio>
    </Sezione>
  );
}

/** I nomi dei tasti, scritti «così», in grassetto: si trovano a colpo
 *  d'occhio quando si cerca il tasto nella pagina vera. */
function Virgolette({ testo }: { testo: string }) {
  const pezzi = testo.split(/(«[^»]+»)/g);
  return (
    <>
      {pezzi.map((pz, i) =>
        pz.startsWith("«") ? (
          <strong key={i} className="font-bold text-ink">
            {pz}
          </strong>
        ) : (
          pz
        ),
      )}
    </>
  );
}

function Guasti() {
  return (
    <Foglio className="divide-y divide-line">
      {SE_NON_VA.map((g) => (
        <details key={g.sintomo} className="group px-5 py-3.5 max-md:px-4">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-[14px] font-bold text-ink max-md:min-h-[44px] max-md:text-[16px] [&::-webkit-details-marker]:hidden">
            {g.sintomo}
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-4 w-4 shrink-0 fill-none stroke-current stroke-2 transition-transform duration-200 group-open:rotate-180 [stroke-linecap:round] [stroke-linejoin:round]"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </summary>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-[13px] leading-relaxed text-ink max-md:text-[15px]">
            {g.cosaFare.map((c) => (
              <li key={c}>
                <Virgolette testo={c} />
              </li>
            ))}
          </ol>
        </details>
      ))}
    </Foglio>
  );
}

function Glossario() {
  return (
    <Foglio>
      <dl className="grid grid-cols-2 max-lg:grid-cols-1">
        {GLOSSARIO.map((t, i) => (
          <div
            key={t.parola}
            className={[
              "flex gap-4 border-line px-5 py-3 max-md:px-4",
              i % 2 === 0 ? "lg:border-r" : "",
              i >= 2 ? "border-t" : i === 1 ? "max-lg:border-t" : "",
              i === GLOSSARIO.length - 1 && i % 2 === 0 ? "lg:col-span-2 lg:border-r-0" : "",
            ].join(" ")}
          >
            <dt className="w-[92px] shrink-0 font-display text-[14px] font-bold text-ink max-md:text-[16px]">
              {t.parola}
            </dt>
            <dd className="min-w-0 text-[13px] leading-relaxed text-ink max-md:text-[15px]">
              {t.significa}
            </dd>
          </div>
        ))}
      </dl>
    </Foglio>
  );
}

type Trovato = { dove: string; href: string; titolo: string; righe: string[] };

/** La ricerca: ogni parola deve esserci (in qualunque ordine), cosi'
 *  «ldv fornitore» trova la voce anche se le parole sono lontane. */
function Risultati({ q }: { q: string }) {
  const trovati = useMemo(() => {
    const parole = q.split(/\s+/).filter(Boolean);
    const tutte = (testo: string) => {
      const t = normalizza(testo);
      return parole.every((p) => t.includes(p));
    };
    const out: Trovato[] = [];
    GIRO_ORDINE.forEach((t) => {
      if (tutte(`${t.nome} ${t.testo}`))
        out.push({ dove: "Come gira un ordine", href: "#giro", titolo: t.nome, righe: [t.testo] });
    });
    REGOLE_ORO.forEach((r) => {
      if (tutte(`${r.titolo} ${r.perche}`))
        out.push({ dove: "Regole d'oro", href: "#regole", titolo: r.titolo, righe: [r.perche] });
    });
    PAGINE.forEach((p) => {
      p.voci.forEach((v) => {
        if (tutte(`${p.nome} ${v.titolo} ${v.passi.join(" ")}`))
          out.push({ dove: p.nome, href: `#${p.id}`, titolo: v.titolo, righe: v.passi });
      });
      if (p.daSolo && tutte(`${p.nome} ${p.daSolo.join(" ")}`))
        out.push({ dove: p.nome, href: `#${p.id}`, titolo: "OM fa da solo", righe: p.daSolo });
    });
    SE_NON_VA.forEach((g) => {
      if (tutte(`${g.sintomo} ${g.cosaFare.join(" ")}`))
        out.push({
          dove: "Se qualcosa non va",
          href: "#guasti",
          titolo: g.sintomo,
          righe: g.cosaFare,
        });
    });
    GLOSSARIO.forEach((t) => {
      if (tutte(`${t.parola} ${t.significa}`))
        out.push({
          dove: "Le parole di OM",
          href: "#parole",
          titolo: t.parola,
          righe: [t.significa],
        });
    });
    return out;
  }, [q]);

  return (
    <section aria-label="Risultati della ricerca" aria-live="polite">
      <p className="mb-3 text-[13px] text-ink max-md:text-[15px]">
        {trovati.length === 0
          ? "Nessun risultato. Prova con un'altra parola, per esempio il nome del tasto."
          : trovati.length === 1
            ? "1 risultato"
            : `${trovati.length} risultati`}
      </p>
      {trovati.length > 0 && (
        <Foglio className="divide-y divide-line">
          {trovati.map((t) => (
            <article key={`${t.dove}-${t.titolo}`} className="px-5 py-4 max-md:px-4">
              <p className="text-[11.5px] font-bold uppercase tracking-[0.06em] text-ink-2 max-md:text-[12.5px] max-md:text-ink">
                {t.dove}
              </p>
              <h3 className="mt-0.5 text-[14px] font-bold text-ink max-md:text-[16px]">
                {t.titolo}
              </h3>
              <ul className="mt-1.5 space-y-1 text-[13px] leading-relaxed text-ink max-md:text-[15px]">
                {t.righe.map((r) => (
                  <li key={r}>
                    <Evidenzia testo={r} q={q} />
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </Foglio>
      )}
    </section>
  );
}

/** Evidenzia le parole cercate. Lavora sul testo normalizzato per trovare
 *  le posizioni, ma mostra l'originale (accenti compresi). */
function Evidenzia({ testo, q }: { testo: string; q: string }): ReactNode {
  const parole = q.split(/\s+/).filter((p) => p.length > 1);
  if (parole.length === 0) return testo;
  const norm = normalizza(testo);
  const segni = new Array<boolean>(testo.length).fill(false);
  for (const p of parole) {
    let i = norm.indexOf(p);
    while (i !== -1) {
      for (let k = i; k < i + p.length; k++) segni[k] = true;
      i = norm.indexOf(p, i + p.length);
    }
  }
  const pezzi: ReactNode[] = [];
  let inizio = 0;
  for (let i = 1; i <= testo.length; i++) {
    if (i === testo.length || segni[i] !== segni[inizio]) {
      const pz = testo.slice(inizio, i);
      pezzi.push(
        segni[inizio] ? (
          <mark key={inizio} className="rounded-sm bg-amber-soft px-0.5 text-ink">
            {pz}
          </mark>
        ) : (
          pz
        ),
      );
      inizio = i;
    }
  }
  return pezzi;
}
